import React, { useContext, useState, useEffect } from 'react';
import { useQuery } from 'react-apollo';
import { FlowContext } from '../../core/flow-engine';
import { CartContextProvider } from '../../context/CartContext/CartContext';
import { RewardsContext } from '../../context/RewardsContext/RewardsContext';
import { ALL_FUNDS_QUERY } from '../../components/FundListFragment/queries';
import FONASUCustomerInformation from '../../components/FONASUCustomerInformation/FONASUCustomerInformation';
import FONASUPortfolioAllocation from '../../components/FONASUPortfolioAllocation/FONASUPortfolioAllocation';
import FONASUCreateCredentials from '../../components/FONASUCreateCredentials/FONASUCreateCredentials';
import { formatFunds } from '../../flows/shared/utils';
import FONASUHelpText from '../../components/FONASUHelpText/FONASUHelpText';
import styles from './FONASUMFRolloverScreen.css';

import Heading from '../../components/Typography/Heading/Heading';
import { MFBeaconButton } from '../../components/Beacon/BeaconComponents';
import AlertMessage from '../../components/AlertMessage/AlertMessage';
import greenCheck from '../../images/green-checkmark.svg';
import { isPublishNotAllowed } from '../FONASUMFTransferScreen/util';
import FONASUMFRolloverFunding from '../../components/FONASUMFRolloverFunding/FONASUMFRolloverFunding';
import AlertMessageWithButton from '../../components/AlertMessageWithButton/AlertMessageWithButton';

const FONASUMFRolloverScreenContents = ({
  children,
  showSuccess,
  editCustomer,
  setEditCustomer,
  editPortfolio,
  setEditPortfolio,
  editFunding,
  setEditFunding,
  showCredentialsSection,
  isValid,
  next,
  alert,
  error
}) => {
  const { data: { allMutualFunds = [] } = {} } = useQuery(ALL_FUNDS_QUERY);

  const { rewardsAvailable } = useContext(RewardsContext);

  // filter out suppressed fund regardless of indirect or direct rollover funding
  const rolloverMutualFunds = allMutualFunds.filter(
    ({ openToNewInvestors, openToSubsequentInvestments }) =>
      openToNewInvestors || (!openToNewInvestors && openToSubsequentInvestments)
  );

  const pageTitle = `Open a Rollover IRA Account`;

  return (
    <>
      {alert && (
        <AlertMessage message="The publish to web function failed and the information was not submitted. Please try again." />
      )}
      <AlertMessageWithButton
        error={error}
        onClick={next('deleteApplication')}
        btnStyle="continue-button"
        btnText="Continue"
      />
      <div className="wrapper">
        {showSuccess && (
          <div className="submit-result-message">
            <img className="green-check" src={greenCheck} alt="Success" />{' '}
            Submitted!
          </div>
        )}
        <Heading text={pageTitle} />
        <FONASUHelpText />

        <div className="review-section">
          <FONASUCustomerInformation
            showSuccess={showSuccess}
            editCustomer={editCustomer}
            setEditCustomer={setEditCustomer}
            showInvestmentAmt
          />
        </div>

        <div className="review-section">
          <FONASUPortfolioAllocation
            showSuccess={showSuccess}
            editPortfolio={editPortfolio}
            setEditPortfolio={setEditPortfolio}
            allMutualFunds={rolloverMutualFunds}
            rewardsAvailable={rewardsAvailable}
            next={next}
            error={error}
          />
        </div>

        <div className="review-section">
          <FONASUMFRolloverFunding
            showSuccess={showSuccess}
            editFunding={editFunding}
            setEditFunding={setEditFunding}
          />
        </div>

        {showCredentialsSection && (
          <div className="review-section">
            <FONASUCreateCredentials
              showSuccess={showSuccess}
              isValid={isValid}
            />
          </div>
        )}

        <div className="btn-container">{children}</div>

        <style jsx>{styles}</style>
      </div>
    </>
  );
};

const checkValid = (infoValid, portfolioValid, fundingValid) =>
  infoValid && portfolioValid && fundingValid;

const doneMode = (editCustomer, editPortfolio, editFunding) =>
  !(editCustomer || editPortfolio || editFunding);

const shouldShowCredentialsSection = usernameExists => !usernameExists;

const FONASUMFRolloverScreen = ({ appState }) => {
  const { next, alert } = useContext(FlowContext);
  const {
    store: {
      app: { newInvestments: selectedFunds, profileApplicationID },
      amount,
      loadedData: {
        infoValid,
        portfolioValid,
        usernameExists,
        fundingValid,
        error,
        customerInfoEditMode,
        portfolioEditMode,
        fundingEditMode,
        loginID
      }
    }
  } = appState;

  const [showSuccess, setShowSuccess] = useState(false);
  const [editCustomer, setEditCustomer] = useState(customerInfoEditMode);
  const [editPortfolio, setEditPortfolio] = useState(portfolioEditMode);
  const [editFunding, setEditFunding] = useState(fundingEditMode);

  useEffect(() => {
    if (profileApplicationID) setShowSuccess(true);
  }, [profileApplicationID]);

  const isValid =
    checkValid(infoValid, portfolioValid, fundingValid) &&
    doneMode(editCustomer, editPortfolio, editFunding);

  const disablePublish = isPublishNotAllowed(
    showSuccess,
    isValid,
    loginID,
    error
  );
  const showCredentialsSection = shouldShowCredentialsSection(usernameExists);

  const formattedSelectedFunds = formatFunds(selectedFunds);

  const handleClick = async () => {
    if (isValid && !showSuccess) {
      next({
        action: 'publish'
      })();
    }
  };

  return (
    <CartContextProvider
      initialTotalAmount={amount}
      initialPercentMode
      initialFunds={formattedSelectedFunds}>
      <FONASUMFRolloverScreenContents
        editCustomer={editCustomer}
        setEditCustomer={setEditCustomer}
        editPortfolio={editPortfolio}
        setEditPortfolio={setEditPortfolio}
        editFunding={editFunding}
        setEditFunding={setEditFunding}
        showSuccess={showSuccess}
        setShowSuccess={setShowSuccess}
        showCredentialsSection={showCredentialsSection}
        isValid={isValid}
        next={next}
        alert={alert}
        error={error}>
        <MFBeaconButton
          data-name="rolloverPublishToWebBtn"
          disabled={disablePublish}
          onClick={handleClick}
          className="beacon-button-margin-override"
          variant="primary"
          label="Publish to Web"
        />
      </FONASUMFRolloverScreenContents>
    </CartContextProvider>
  );
};

export default FONASUMFRolloverScreen;
